import {useAppTranslation} from "services/i18n";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select
} from "@mui/material";
import {Close} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import {TextFormFieldPlain} from "components/form/TextFormField";
import {CheckboxPlain} from "components/form/CheckboxFormField";
import {isSearchWithinSubject} from "components/DataGrid";
import {useAppDispatch} from "store";
import {fetchCatalogueQuestions, fetchCatalogues} from "store/catalogues";
import {CatalogueQuestionType, CatalogueType} from "helpers/editorTest";
import {Link} from "components/clickable/Link";
import {shorten} from "utils/utils";

interface Props {
  currentQuestionIds: number[];
  onSuccess: (questionIds: number[]) => unknown;
  onClose: () => unknown;
}

export const EditorTestCatalogueQuestionsDialog = (props: Props) => {
  const {currentQuestionIds, onSuccess, onClose} = props;

  const dispatch = useAppDispatch();
  const t = useAppTranslation();

  const [catalogues, setCatalogues] = useState<CatalogueType[] | undefined>();
  const [activeCatalogueId, setActiveCatalogueId] = useState<number | undefined>();
  const [questions, setQuestions] = useState<CatalogueQuestionType[] | undefined>();

  const [search, setSearch] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [clickCount, setClickCount] = useState<number>(0);

  const handleFetchCatalogues = useCallback(async () => {
    const catalogueItems = (await dispatch(fetchCatalogues({})))?.payload as CatalogueType[];
    setCatalogues(catalogueItems);
    if (catalogueItems[0]?.id) {
      setActiveCatalogueId(catalogueItems[0].id)
    }
  }, [dispatch]);

  const handleFetchQuestions = useCallback(async (catalogueId: number) => {
    setQuestions(undefined);
    const items = (await dispatch(fetchCatalogueQuestions({
      questionCatalogueId: catalogueId,
      sort: 'name:asc',
      perPage: 1000,
      status: 'active'
    })))?.payload as CatalogueQuestionType[];
    setQuestions(items);
  }, [dispatch]);

  const handleSave = useCallback(() => {
    onSuccess(selectedIds);
  }, [selectedIds, onSuccess]);

  const createItems = useCallback((questions: CatalogueQuestionType[], items: JSX.Element[]) => {
    questions
      .forEach((question) => {
        if (search && !isSearchWithinSubject(search, question.name)) {
          return;
        }
        const isAlready = currentQuestionIds.indexOf(question.id) >= 0;
        const isSelected = isAlready || selectedIds.indexOf(question.id) >= 0;
        items.push(<Box
          key={question.id} className={'tw-flex tw-flex-row tw-items-center tw-justify-items-stretch'}>
          <div className={'tw-pr-24 tw-flex tw-self-center'}>
            <CheckboxPlain
              name={'cat-' + question.id}
              currentValue={isSelected}
              onChange={() => {
                const pos = selectedIds.indexOf(question.id);
                if (pos >= 0) {
                  selectedIds.splice(pos, 1);
                } else {
                  selectedIds.push(question.id);
                }
                setSelectedIds(selectedIds.sort());
                setClickCount(clickCount + 1);
              }}
              disabled={isAlready}
              color={isAlready ? 'info' : undefined}
            />
          </div>
          <p className={'tw-flex tw-w-full'}>{shorten(100)(question.name)}</p>
        </Box>)
      });

  }, [currentQuestionIds, clickCount, search, selectedIds]);

  const questionItems = useMemo(() => {
    if (questions === undefined) {
      return [<Box key={0} className={'tw-h-100'}><LinearProgress/></Box>];
    }

    const top = questions.filter(q => selectedIds.indexOf(q.id) >= 0 || currentQuestionIds.indexOf(q.id) >= 0);
    const bottom = questions.filter(q => !top.find(t => t.id === q.id));

    const items: JSX.Element[] = [];
    createItems(top, items);
    createItems(bottom, items);
    return items;

  }, [questions, currentQuestionIds, selectedIds, createItems]);

  useEffect(() => {
    if (activeCatalogueId) {
      handleFetchQuestions(activeCatalogueId).then();
    }
  }, [activeCatalogueId, handleFetchQuestions]);

  useEffect(() => {
    handleFetchCatalogues().then();
  }, [handleFetchCatalogues]);

  return (
    <Dialog open={true} onClose={onClose} PaperComponent={Paper}>
      <DialogTitle className={'tw-flex tw-w-full tw-justify-between tw-items-center'}>
        <span>{t('catalogues.categories.form.questionSearch')}</span>
        <IconButton onClick={onClose} sx={{color: 'inherit'}}><Close/></IconButton>
      </DialogTitle>
      <DialogContent>
        {catalogues === undefined && <LinearProgress/>}
        {catalogues?.length === 0 && <>
            <p>{t('editorTest.catalogueQuestions.emptyCatalogues')}</p>
            <Link
                kind="bare"
                className="tw-mb-8 tw-pt-8 tw-pb-10 tw-pl-32 tw-pr-32 tw-inline-block tw-rounded-lg tw-border tw-border-dark tw-bg-dark tw-text-center tw-text-white"
                href={"/#/catalogues"}
            >
              {t(`editorTest.catalogueQuestions.goToCatalogue`)}
            </Link>
        </>}
        {!!catalogues?.length && <Grid container className={'tw-pt-16'} spacing={2}>
            <Grid size={12}>
              {!!catalogues?.length && <FormControl size="small">
                  <Select
                      displayEmpty={true}
                      value={activeCatalogueId}
                      onChange={(e, v) => {
                        setActiveCatalogueId(+e.target.value);
                      }}>
                    {catalogues?.map((c, i) => {
                      return <MenuItem key={i} value={c.id}>{c.title}</MenuItem>;
                    })}
                  </Select>
              </FormControl>}
            </Grid>
          {(questionItems.length > 1 || !!search) && <Grid size={12}>
              <TextFormFieldPlain
                  name={'search'}
                  currentValue={search}
                  onChange={setSearch}
                  label={t('catalogues.questions.searchTitle')}
                  placeholder={t('catalogues.questions.searchPlaceholder')}
              />
          </Grid>}
            <Grid size={12}>
              {questions !== undefined && !questionItems.length && <>
                  <p>{t('editorTest.catalogueQuestions.emptyQuestions')}</p>
                  <Link
                      kind="bare"
                      className="tw-mb-8 tw-pt-8 tw-pb-10 tw-pl-32 tw-pr-32 tw-inline-block tw-rounded-lg tw-border tw-border-dark tw-bg-dark tw-text-center tw-text-white"
                      href={"/#/catalogues/" + activeCatalogueId + "/questions"}
                  >
                    {t(`editorTest.catalogueQuestions.goToQuestions`)}
                  </Link>
              </>}
              {questionItems}
            </Grid>
        </Grid>}
      </DialogContent>
      <DialogActions>
        <Button color={'inherit'} onClick={onClose}>
          {t('form.close')}
        </Button>
        {!!activeCatalogueId && !!selectedIds.length && <Button color={'primary'} onClick={handleSave}>
          {t('editorTest.catalogueQuestions.saveQuestions')}
        </Button>}
      </DialogActions>
    </Dialog>
  );
};
